import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import NotificationRing from "../../assets/svg/NotificationRing.svg";
import CloseButton from "../../assets/svg/CloseButton.svg";
import Bin from "../../assets/svg/Bin.svg";
import Tooltip from "../reusable/Tooltip";
import axios from "axios";
import { API } from "../../shared/api/Api";
import { useNavigate } from "react-router-dom";
import { showToast, trimString } from "../../utils/utils";
import {
  getNotificationNavigation,
  handleNotificationNavigation,
  getNotificationDisplay,
  isUrgentNotification,
  getNotificationIcon,
} from "../../utils/notificationUtils";

const RealTimeNotification = ({ notification, isVisible, slideIn, user }) => {
  const [hideNotification, setHideNotification] = useState(false);
  const navigate = useNavigate();
  const lang = user?.lang || "en";

  // Parse notification options
  let parsedOptions = {};
  try {
    parsedOptions = notification.notify.options
      ? JSON.parse(notification.notify.options)
      : {};
  } catch (error) {
    console.error("Failed to parse notification options:", error);
  }

  // Get navigation and display data
  const navigationData = getNotificationNavigation(parsedOptions, user);
  const displayConfig = getNotificationDisplay(parsedOptions, user);
  const isUrgent = isUrgentNotification(parsedOptions);
  const icon = getNotificationIcon(parsedOptions);

  if (hideNotification || !isVisible) return null;

  /**
   * Mark notification as clicked
   */
  const handleNotificationClicked = async (notificationId) => {
    try {
      const { data } = await axios.put(
        `${API.Notifications.notificationStatusUpdate}/${notificationId}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            lang: lang,
          },
        }
      );

      if (data.status !== 200 || data.errorCode === 1) {
        showToast("Error", data.message, "error");
      } else {
        setHideNotification(true);
      }
    } catch (error) {
      console.error("Error marking notification as clicked:", error);
    }
  };

  /**
   * Delete notification
   */
  const handleNotificationDelete = async (notificationId) => {
    try {
      const { data } = await axios.delete(
        `${API.Notifications.deleteNotification}/${notificationId}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            lang: lang,
          },
        }
      );

      if (data.status === 200 || data.errorCode === 0) {
        setHideNotification(true);
        showToast("Success", data.message, "success");
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
      showToast("Error", "Failed to delete notification", "error");
    }
  };

  /**
   * Handle notification click and navigation
   */
  const handleNavigateNotification = async () => {
    await handleNotificationClicked(notification.notify.id);

    // Navigate based on notification type
    if (navigationData) {
      handleNotificationNavigation(navigationData, navigate);
    } else {
      console.warn("No navigation data available for this notification");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ x: "120%" }}
        animate={{ x: slideIn ? 0 : "120%" }}
        exit={{ x: "120%" }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        className={`fixed bottom-10 right-10 z-50 w-full max-w-[90vw] md:max-w-[557px] min-h-[157px] p-4 md:p-5 rounded-xl shadow-2xl border-2 ${
          isUrgent
            ? `${displayConfig.bgColor} ${displayConfig.borderColor} ${
                displayConfig.animate ? "animate-pulse" : ""
              }`
            : "bg-white border-gray-200"
        }`}
      >
        {/* Left section: Icon and content */}
        <div
          className="flex items-start space-x-4 cursor-pointer flex-1 pr-12"
          onClick={handleNavigateNotification}
        >
          {/* Notification icon */}
          <div className="relative flex-shrink-0">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-2xl shadow-lg">
              <span>{icon}</span>
            </div>
            
            {/* Badge indicator */}
            {displayConfig.badgeText && (
              <div className="absolute -top-2 -right-2">
                <span
                  className={`px-2 py-1 text-xs rounded-full font-bold shadow-md ${
                    displayConfig.badgeColor
                  } ${displayConfig.animate ? "animate-pulse" : ""}`}
                >
                  {displayConfig.badgeText}
                </span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            {notification.notify.title_en || notification.notify.title_ar ? (
              <h3 className="text-base md:text-lg font-bold text-gray-900 mb-1">
                {lang === "en"
                  ? trimString(notification.notify.title_en, 50)
                  : trimString(notification.notify.title_ar, 50)}
              </h3>
            ) : null}

            {/* Content text */}
            <Tooltip
              content={
                lang === "en"
                  ? notification.notify.content_en
                  : notification.notify.content_ar
              }
            >
              <p className="text-sm md:text-base text-gray-700 line-clamp-2 mb-2">
                {trimString(
                  lang === "en"
                    ? notification.notify.content_en
                    : notification.notify.content_ar,
                  100
                )}
              </p>
            </Tooltip>

            {/* Score display for graded exams */}
            {displayConfig.scoreInfo && (
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-gray-600">
                  Score:
                </span>
                <span
                  className={`text-sm font-bold ${
                    displayConfig.isPassed ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {displayConfig.scoreInfo}
                </span>
                {displayConfig.percentage && (
                  <span className="text-xs text-gray-500">
                    ({displayConfig.percentage})
                  </span>
                )}
                {displayConfig.passStatus && (
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      displayConfig.isPassed
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {displayConfig.passStatus}
                  </span>
                )}
              </div>
            )}

            {/* Deadline info for reminders */}
            {displayConfig.hoursRemaining !== undefined && (
              <div className="flex items-center gap-1 text-xs text-orange-600 mb-2">
                <span className="font-medium">⏰</span>
                <span>
                  {displayConfig.hoursRemaining === 24
                    ? "Deadline in 1 day"
                    : `Deadline in ${displayConfig.hoursRemaining} hours`}
                </span>
              </div>
            )}

            {/* Action button */}
            {displayConfig.actionText && navigationData && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigateNotification();
                }}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg ${
                  isUrgent
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-blue-500 hover:bg-blue-600"
                } ${displayConfig.animate ? "animate-pulse" : ""}`}
              >
                {displayConfig.actionText}
              </button>
            )}
          </div>
        </div>

        {/* Right section: Close and action buttons */}
        <div className="absolute top-4 right-4 flex flex-col items-end space-y-3">
          {/* Close button */}
          <button
            onClick={() => setHideNotification(true)}
            className="p-1 hover:bg-gray-200 rounded-full transition-colors"
            title="Close"
          >
            <img
              className="w-5 h-5 opacity-70 hover:opacity-100 transition-opacity"
              src={CloseButton}
              alt="Close"
            />
          </button>

          {/* Bottom actions */}
          <div className="flex items-center space-x-3 mt-16">
            {/* Mark as read */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNotificationClicked(notification.notify.id);
              }}
              className="text-xs text-blue-600 hover:text-blue-700 underline transition-colors"
              title="Mark as read"
            >
              Mark as read
            </button>

            {/* Delete button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleNotificationDelete(notification.notify.id);
              }}
              className="p-1 hover:bg-red-50 rounded-full transition-colors group"
              title="Delete"
            >
              <img
                className="w-5 h-5 opacity-50 group-hover:opacity-100 transition-opacity"
                src={Bin}
                alt="Delete"
              />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default RealTimeNotification;
